import { useQuery } from '@tanstack/react-query';

export interface AddressComponents {
  adressomrade: string;
  adressplatsnummer: string | null;
  bokstavstillagg?: string | null;
  kommun: string;
  kommundel: string;
  postnummer: number;
  postort: string;
}

export interface AutocompleteAddressOption {
  adress: string;
  adressComponents: AddressComponents;
  objektidentitet: string;
}

export interface UseAddressAutocompleteOptions {
  enabled?: boolean;
  minSearchLength?: number;
  staleTime?: number;
}

export type AddressSearchFunction = (
  searchQuery: string,
  signal?: AbortSignal
) => Promise<AutocompleteAddressOption[]>;

// Helper function to format address for display
export const formatAddressLabel = (address: AutocompleteAddressOption): string => {
  const { adressomrade, adressplatsnummer, bokstavstillagg, postnummer, postort } = address.adressComponents;

  const addressNumber = `${adressplatsnummer || ''}${bokstavstillagg || ''}`;
  const postalInfo = `${postnummer} ${postort}`;

  const adressNumberNotEmpty = addressNumber.trim().length !== 0;

  return `${adressomrade}${adressNumberNotEmpty ? ` ${addressNumber}` : ''}, ${postalInfo}`;
};

export const useAddressAutocomplete = (
  searchQuery: string,
  searchFunction: AddressSearchFunction,
  options: UseAddressAutocompleteOptions = {}
) => {
  const {
    enabled = true,
    minSearchLength = 2,
    staleTime = 1000 * 60 * 5, // 5 minutes
  } = options;

  return useQuery({
    queryKey: ['address-autocomplete', searchQuery],
    queryFn: ({ signal }) => searchFunction(searchQuery, signal),
    enabled: enabled && searchQuery.length >= minSearchLength,
    staleTime,
    gcTime: 1000 * 60 * 10, // 10 minutes
    // Retry only on network errors, not on API errors
    retry: (failureCount, error) => {
      return failureCount < 2 && error.message.includes('network');
    }
  });
};